import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ClientContactPicker } from "@/components/ClientContactPicker";
import StateCountyPicker from "@/components/StateCountyPicker";
import { ScopeEditor } from "@/components/ScopeEditor";
import { scopesFromDoc } from "@/lib/disciplines";
import { SimilarProjectsWarning } from "@/components/SimilarProjectsWarning";
import { ProjectIdentityFields, useClients } from "@/components/ProjectIdentity";
import { Upload, FileText, ExternalLink } from "lucide-react";

// Push a single proposal file onto the bid via the chunked-upload endpoints (same path the
// Documents tab uses). Ingest runs in the background; returns the upload job id.
async function uploadProposal(bidId, file) {
  const { data: init } = await api.post(`/bids/${bidId}/uploads/init`, {});
  const buf = await file.arrayBuffer();
  await api.put(`/uploads/${init.upload_id}/chunk`, buf, { headers: { "Content-Type": "application/octet-stream" } });
  const { data } = await api.post(`/bids/${bidId}/uploads/${init.upload_id}/complete`, {
    filename: file.name, content_type: file.type || "application/pdf",
  });
  return data?.job_id;
}

const EMPTY = { client_id: null, project: "", scope: null, contact_id: null, contact_name: "", contact_email: "", contact_phone: "", scopes: [], capacity_mw: "", lump_sum: "", state: "", county: "", sent_at: "", note: "", revision_label: "", what_changed: "" };

const partialGap = (scopes) => (scopes || []).some((s) => s.completeness === "partial" && !(s.completeness_note || "").trim());

// Log a project priced OUTSIDE Grid-Forge (create), or revise an existing one (editBid).
export default function ExternalBidDialog({ open, onOpenChange, onCreated, editBid = null, convertBid = null }) {
  const isEdit = !!editBid;
  const isConvert = !!convertBid && !editBid;
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const clients = useClients();

  useEffect(() => {
    if (!open) return;
    setFile(null);
    if (isEdit) {
      setForm({
        ...EMPTY,
        scopes: scopesFromDoc(editBid),
        capacity_mw: editBid.capacity_mw ?? "", lump_sum: editBid.stored_total ?? "",
        state: editBid.state || "", note: editBid.external_note || "",
        sent_at: editBid.sent_at ? String(editBid.sent_at).slice(0, 10) : "",
      });
    } else if (isConvert) {
      // Pricing an existing pipeline project elsewhere: prefill from the bid; lump sum is blank
      // (the final out-of-system price the user is about to enter).
      setForm({
        ...EMPTY,
        capacity_mw: convertBid.capacity_mw ?? "", state: convertBid.state || "",
        sent_at: new Date().toISOString().slice(0, 10),
      });
      api.get(`/bids/${convertBid.bid_id || convertBid.id}`).then(({ data }) => {
        setForm((s) => ({
          ...s, scopes: scopesFromDoc(data), capacity_mw: data.capacity_mw ?? s.capacity_mw, state: data.state || s.state,
        }));
      }).catch(() => {});
    } else {
      setForm(EMPTY);
    }
  }, [open, isEdit, isConvert, editBid, convertBid]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!isEdit && !isConvert) {
      if (!form.client_id) { toast.error("Pick the client from the list."); return; }
      if (!form.project.trim()) { toast.error("Enter the site name."); return; }
      if (!form.scope) { toast.error("Pick the scope (plain Electrical needs DC or AC)."); return; }
    }
    if (!isEdit) {
      if (!(Number(form.capacity_mw) > 0)) { toast.error("Enter the capacity in MW."); return; }
    }
    if (partialGap(form.scopes)) { toast.error("Describe each partial scope first."); return; }
    if (!(Number(form.lump_sum) > 0)) { toast.error("Enter the fully-loaded lump-sum price."); return; }
    if (isConvert && !file) { toast.error("Attach the proposal you sent the client."); return; }
    if (isEdit && form.revision_label && !/^R\d+$/i.test(form.revision_label.trim())) { toast.error("A round label reads R1, R2 …"); return; }
    setSaving(true);
    try {
      let bid;
      if (isConvert) {
        const cid = convertBid.bid_id || convertBid.id;
        let jobId;
        try { jobId = await uploadProposal(cid, file); }
        catch { toast.error("The proposal upload failed — nothing was changed. Try again."); return; }
        const { data } = await api.post(`/bids/${cid}/price-elsewhere`, {
          lump_sum: Number(form.lump_sum), capacity_mw: Number(form.capacity_mw) || undefined,
          note: form.note, sent_at: form.sent_at || undefined, proposal_job_id: jobId,
          scopes: form.scopes && form.scopes.length ? form.scopes : undefined,
        });
        bid = data;
      } else if (isEdit) {
        const { data } = await api.post(`/bids/${editBid.bid_id}/external-update`, {
          lump_sum: Number(form.lump_sum), capacity_mw: Number(form.capacity_mw) || undefined,
          note: form.note, sent_at: form.sent_at || undefined,
          scopes: form.scopes && form.scopes.length ? form.scopes : undefined,
          revision_label: form.revision_label.trim().toUpperCase() || undefined,
          what_changed: form.what_changed.trim() || undefined,
        });
        bid = data;
      } else {
        const { data } = await api.post("/bids/external", {
          client_id: form.client_id, project: form.project.trim(), scope: form.scope,
          contact_id: form.contact_id || undefined, contact_name: form.contact_name || undefined,
          contact_email: form.contact_email || undefined, contact_phone: form.contact_phone || undefined,
          capacity_mw: Number(form.capacity_mw), lump_sum: Number(form.lump_sum),
          state: form.state || undefined, sent_at: form.sent_at || undefined,
          note: form.note || undefined,
        });
        bid = data;
      }
      if (file && !isConvert) {
        try { await uploadProposal(bid.bid_id, file); }
        catch { toast.error("Saved, but the proposal upload failed — add it under Documents."); }
      }
      toast.success(isConvert ? "Moved to Sent — now tracked as priced elsewhere" : isEdit ? "Lump sum updated" : "Logged — tracking it now");
      onCreated?.(bid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="external-bid-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ExternalLink className="w-4 h-4 text-indigo-600" />
            {isConvert ? "Price this project elsewhere" : isEdit ? "Update lump sum" : "Log a bid priced elsewhere"}
          </DialogTitle>
          <DialogDescription>
            {isConvert
              ? "For a pipeline project you priced OUTSIDE Grid-Forge. Enter the final, fully-loaded lump sum — it moves to Sent and we keep tracking it. No markups, tax or contingency are applied. You can switch it back to an in-house estimate anytime."
              : isEdit
              ? "Revise the fully-loaded lump sum, scopes and MW (and optionally attach an updated proposal). Prior values are kept in the history."
              : "For a project you priced OUTSIDE Grid-Forge. The lump sum is the final, fully-loaded price — no markups, tax or contingency are ever applied."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[62vh] overflow-y-auto pr-1">
          {(!isEdit && !isConvert) ? (
            <>
              <ProjectIdentityFields value={form} onChange={(v) => setForm((s) => ({ ...s, ...v }))} idPrefix="external" clients={clients} />
              <ClientContactPicker contactOnly
                clientId={form.client_id} clientName={(clients.find((c) => c.client_id === form.client_id) || {}).name || ""}
                contactId={form.contact_id} contactName={form.contact_name}
                onClient={(c) => setForm((s) => ({ ...s, client_id: c.client_id }))}
                onContact={(ct) => setForm((s) => ({ ...s, contact_id: ct.contact_id, contact_name: ct.name || "", contact_email: ct.email || "", contact_phone: ct.phone || "" }))}
              />
              <SimilarProjectsWarning name={form.project} company={(clients.find((c) => c.client_id === form.client_id) || {}).name || ""} onClose={() => onOpenChange(false)} />
            </>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm text-slate-600">
              <div className="font-medium text-slate-800">{(editBid || convertBid)?.name}</div>
              <div className="text-xs text-slate-500">{(editBid || convertBid)?.bid_number ? `${(editBid || convertBid).bid_number} · ` : ""}{(editBid || convertBid)?.client_name || "No client"}</div>
            </div>
          )}

          {isEdit && (
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-[#003E87]/20 bg-[#003E87]/[0.03] p-3">
              <div>
                <Label className="text-xs">New round (optional)</Label>
                <Input data-testid="external-round-label" value={form.revision_label} onChange={(e) => set("revision_label", e.target.value)} placeholder={`R${Math.max(1, Number(editBid?.revision || 1))}`} />
                <div className="text-[11px] text-slate-400 mt-1">Opens Sent to client/{form.revision_label.trim().toUpperCase() || "R#"}; the earlier round's files stay, flagged superseded.</div>
              </div>
              <div>
                <Label className="text-xs">What changed</Label>
                <Input data-testid="external-what-changed" value={form.what_changed} onChange={(e) => set("what_changed", e.target.value)} placeholder="Added MV scope, resent 10/14" />
              </div>
            </div>
          )}

          {(isEdit || isConvert) && (
            <div>
              <Label className="text-xs">Scopes</Label>
              <div className="mt-1.5">
                <ScopeEditor value={form.scopes || []} onChange={(next) => set("scopes", next)} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Capacity (MW) {!isEdit && "*"}</Label>
              <Input data-testid="external-mw" type="number" step="any" value={form.capacity_mw} onChange={(e) => set("capacity_mw", e.target.value)} placeholder="150" />
            </div>
            <div>
              <Label className="text-xs">Lump-sum price ($) *</Label>
              <Input data-testid="external-lump" type="number" step="any" value={form.lump_sum} onChange={(e) => set("lump_sum", e.target.value)} placeholder="e.g. 15048562" />
            </div>
          </div>

          {!isEdit && !isConvert && (
            <StateCountyPicker idPrefix="external" state={form.state} county={form.county}
              onChange={({ state, county }) => setForm((s) => ({ ...s, state, county }))} />
          )}

          <div>
            <Label className="text-xs">Date sent</Label>
            <Input data-testid="external-sent-at" type="date" value={form.sent_at} onChange={(e) => set("sent_at", e.target.value)} />
          </div>

          <div>
            <Label className="text-xs">
              {isConvert ? <>Proposal sent to the client <span className="text-rose-500">*</span></> : `Proposal file ${isEdit ? "(optional — updated proposal)" : "(optional)"}`}
            </Label>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.xlsx,.png,.jpg,.jpeg" className="hidden" data-testid="external-proposal-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="external-proposal-pick"
              className={`mt-1 w-full flex items-center gap-2 text-sm border border-dashed rounded-md px-3 py-2 hover:border-[#003E87] hover:text-[#003E87] transition ${isConvert && !file ? "border-rose-300 bg-rose-50/40 text-rose-700" : "border-slate-300 text-slate-600"}`}>
              {file ? <FileText className="w-4 h-4 text-[#003E87]" /> : <Upload className="w-4 h-4" />}
              <span className="truncate">{file ? file.name : "Upload the proposal you sent the client"}</span>
            </button>
            {isConvert && !file && <div className="text-[11px] text-rose-600 mt-1" data-testid="external-proposal-required">Required — attach the PDF you sent so the price is backed by the document.</div>}
          </div>

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea data-testid="external-note" rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Anything worth remembering about this submission." />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="external-submit" onClick={submit} disabled={saving} className="bg-[#003E87] hover:bg-[#00306a] text-white font-semibold">
            {saving ? "Saving…" : isConvert ? "Mark priced elsewhere" : isEdit ? "Save changes" : "Log & track it"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
